import { INTEGRATIONS_API_URL } from '@/api/integrations';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import type {
  StudioGetSensors3pQuery,
  StudioGetSensors3pQueryVariables,
  StudioGetGateways3pQuery,
  StudioGetGateways3pQueryVariables,
  StudioCreateSensors3pMutation,
  StudioCreateSensors3pMutationVariables,
  StudioUpdateSensors3pMutation,
  StudioUpdateSensors3pMutationVariables,
  StudioDeleteSensors3pMutation,
  StudioDeleteSensors3pMutationVariables,
  StudioCreateGateways3pMutation,
  StudioCreateGateways3pMutationVariables,
  StudioUpdateGateways3pMutation,
  StudioUpdateGateways3pMutationVariables,
  StudioDeleteGateways3pMutation,
  StudioDeleteGateways3pMutationVariables,
} from '@/.generated/graphql/integrations';
import type { Sensor3P, Gateway3P } from './useGetState';
import { toSensor3PDeviceType } from './useGetState';

const GET_SENSORS_3P = gql`
  query StudioGetSensors3p($floor_ids: [String!]) {
    sensors3p(floor_ids: $floor_ids) {
      id
      name
      provider
      device_type
      floor_id
      location
      external_device_id
      spot_id
      gateway_3p_id
      height
      orientation_x_rad
      orientation_y_rad
      room_id
      note
      tags {
        id
        name
      }
      deprovisioned_at
      provisioned_at
    }
  }
`;

const GET_GATEWAYS_3P = gql`
  query StudioGetGateways3p($floor_ids: [String!]) {
    gateways3p(floor_ids: $floor_ids) {
      id
      name
      provider
      floor_id
      location
      external_gateway_id
      custom_id
      room_id
      note
      tags {
        id
        name
      }
      provisioned_at
    }
  }
`;

const CREATE_SENSORS_3P = gql`
  mutation StudioCreateSensors3p($sensors: [CreateSensor3PInput!]!) {
    createSensors3p(sensors: $sensors) {
      id
      name
      provider
      device_type
      floor_id
      location
      external_device_id
      spot_id
      gateway_3p_id
      height
      orientation_x_rad
      orientation_y_rad
      room_id
      note
      tags {
        id
        name
      }
    }
  }
`;

const UPDATE_SENSORS_3P = gql`
  mutation StudioUpdateSensors3p($sensors: [UpdateSensor3PInput!]!) {
    updateSensors3p(sensors: $sensors) {
      id
      name
      provider
      device_type
      floor_id
      location
      external_device_id
      spot_id
      gateway_3p_id
      height
      orientation_x_rad
      orientation_y_rad
      room_id
      note
      tags {
        id
        name
      }
    }
  }
`;

const DELETE_SENSORS_3P = gql`
  mutation StudioDeleteSensors3p($ids: [String!]!) {
    deleteSensors3p(ids: $ids) {
      id
    }
  }
`;

const CREATE_GATEWAYS_3P = gql`
  mutation StudioCreateGateways3p($gateways: [CreateGateway3PInput!]!) {
    createGateways3p(gateways: $gateways) {
      id
      name
      provider
      floor_id
      location
      external_gateway_id
      custom_id
      room_id
      note
      tags {
        id
        name
      }
    }
  }
`;

const UPDATE_GATEWAYS_3P = gql`
  mutation StudioUpdateGateways3p($gateways: [UpdateGateway3PInput!]!) {
    updateGateways3p(gateways: $gateways) {
      id
      name
      provider
      floor_id
      location
      external_gateway_id
      custom_id
      room_id
      note
      tags {
        id
        name
      }
    }
  }
`;

const DELETE_GATEWAYS_3P = gql`
  mutation StudioDeleteGateways3p($ids: [String!]!) {
    deleteGateways3p(ids: $ids) {
      id
    }
  }
`;

export function useGetSensors3pGateways3p({ spaceId, skip }: { spaceId?: string; skip?: boolean }) {
  const shouldSkip = skip || !spaceId;

  const { data: sensorsData, refetch: refetchSensors } = useQuery<
    StudioGetSensors3pQuery,
    StudioGetSensors3pQueryVariables
  >(GET_SENSORS_3P, {
    variables: { floor_ids: spaceId ? [spaceId] : undefined },
    skip: shouldSkip,
    context: { uri: INTEGRATIONS_API_URL },
  });

  const { data: gatewaysData, refetch: refetchGateways } = useQuery<
    StudioGetGateways3pQuery,
    StudioGetGateways3pQueryVariables
  >(GET_GATEWAYS_3P, {
    variables: { floor_ids: spaceId ? [spaceId] : undefined },
    skip: shouldSkip,
    context: { uri: INTEGRATIONS_API_URL },
  });

  // useMemo keyed on data object prevents infinite re-render when parent re-renders.
  // Same pattern as useGetSpots — omitting this dependency causes a render loop.
  const sensors3p: Sensor3P[] = useMemo(
    () =>
      (sensorsData?.sensors3p ?? [])
        .filter(
          (s): s is NonNullable<typeof s> =>
            s !== null &&
            s.id.startsWith('sensor3p_') &&
            s.deprovisioned_at == null &&
            Array.isArray(s.location) &&
            s.location.length === 2 &&
            typeof s.location[0] === 'number' &&
            typeof s.location[1] === 'number',
        )
        .map((s) => ({
          id: s.id,
          name: s.name ?? 'Unnamed sensor',
          provider: s.provider,
          device_type: toSensor3PDeviceType(s.device_type),
          floor_id: s.floor_id,
          location: [s.location![0] as number, s.location![1] as number] as [number, number],
          external_device_id: s.external_device_id ?? undefined,
          spot_id: s.spot_id ?? undefined,
          gateway_3p_id: s.gateway_3p_id ?? undefined,
          height: s.height ?? undefined,
          orientation_x_rad: s.orientation_x_rad ?? undefined,
          orientation_y_rad: s.orientation_y_rad ?? undefined,
          room_id: s.room_id ?? undefined,
          note: s.note ?? undefined,
          tags: s.tags?.filter((t): t is NonNullable<typeof t> => t !== null) ?? undefined,
          provisioned_at: s.provisioned_at ?? undefined,
        })),
    [sensorsData],
  );

  const gateways3p: Gateway3P[] = useMemo(
    () =>
      (gatewaysData?.gateways3p ?? [])
        .filter(
          (g): g is NonNullable<typeof g> =>
            g !== null &&
            g.id.startsWith('gateway3p_') &&
            Array.isArray(g.location) &&
            g.location.length === 2 &&
            typeof g.location[0] === 'number' &&
            typeof g.location[1] === 'number',
        )
        .map((g) => ({
          id: g.id,
          name: g.name ?? 'Unnamed gateway',
          provider: g.provider,
          floor_id: g.floor_id,
          location: [g.location![0] as number, g.location![1] as number] as [number, number],
          external_gateway_id: g.external_gateway_id,
          custom_id: g.custom_id ?? undefined,
          room_id: g.room_id ?? undefined,
          note: g.note ?? undefined,
          tags: g.tags?.filter((t): t is NonNullable<typeof t> => t !== null) ?? undefined,
          provisioned_at: g.provisioned_at ?? undefined,
        })),
    [gatewaysData],
  );

  return {
    sensors3p,
    gateways3p,
    refetchSensors3p: refetchSensors,
    refetchGateways3p: refetchGateways,
  };
}

/** Fetches all sensor3p + gateway3p across multiple floors (for device management page).
 *  Skips when floorIds is empty (e.g. hierarchy not yet loaded). */
export function useGetSensors3pGateways3pAll({
  floorIds,
  skip,
}: {
  floorIds: string[];
  skip?: boolean;
}) {
  const shouldSkip = skip || floorIds.length === 0;

  const { data: sensorsData, loading: sensorsLoading } = useQuery<
    StudioGetSensors3pQuery,
    StudioGetSensors3pQueryVariables
  >(GET_SENSORS_3P, {
    variables: { floor_ids: floorIds },
    skip: shouldSkip,
    context: { uri: INTEGRATIONS_API_URL },
  });

  const { data: gatewaysData, loading: gatewaysLoading } = useQuery<
    StudioGetGateways3pQuery,
    StudioGetGateways3pQueryVariables
  >(GET_GATEWAYS_3P, {
    variables: { floor_ids: floorIds },
    skip: shouldSkip,
    context: { uri: INTEGRATIONS_API_URL },
  });

  // Include sensors without a valid location (device not placed on floor plan) —
  // device management page shows all provisioned devices, not just canvas-placed ones.
  const sensors3p: Sensor3P[] = useMemo(
    () =>
      (sensorsData?.sensors3p ?? [])
        .filter(
          (s): s is NonNullable<typeof s> =>
            s !== null && s.id.startsWith('sensor3p_') && s.deprovisioned_at == null,
        )
        .map((s) => ({
          id: s.id,
          name: s.name ?? 'Unnamed sensor',
          provider: s.provider,
          device_type: toSensor3PDeviceType(s.device_type),
          floor_id: s.floor_id,
          location: (Array.isArray(s.location) && s.location.length === 2
            ? [s.location[0], s.location[1]]
            : [0, 0]) as [number, number],
          external_device_id: s.external_device_id ?? undefined,
          spot_id: s.spot_id ?? undefined,
          gateway_3p_id: s.gateway_3p_id ?? undefined,
          height: s.height ?? undefined,
          orientation_x_rad: s.orientation_x_rad ?? undefined,
          orientation_y_rad: s.orientation_y_rad ?? undefined,
          room_id: s.room_id ?? undefined,
          note: s.note ?? undefined,
          tags: s.tags?.filter((t): t is NonNullable<typeof t> => t !== null) ?? undefined,
          provisioned_at: s.provisioned_at ?? undefined,
        })),
    [sensorsData],
  );

  const gateways3p: Gateway3P[] = useMemo(
    () =>
      (gatewaysData?.gateways3p ?? [])
        .filter((g): g is NonNullable<typeof g> => g !== null && g.id.startsWith('gateway3p_'))
        .map((g) => ({
          id: g.id,
          name: g.name ?? 'Unnamed gateway',
          provider: g.provider,
          floor_id: g.floor_id,
          location: (Array.isArray(g.location) && g.location.length === 2
            ? [g.location[0], g.location[1]]
            : [0, 0]) as [number, number],
          external_gateway_id: g.external_gateway_id,
          custom_id: g.custom_id ?? undefined,
          room_id: g.room_id ?? undefined,
          note: g.note ?? undefined,
          tags: g.tags?.filter((t): t is NonNullable<typeof t> => t !== null) ?? undefined,
          provisioned_at: g.provisioned_at ?? undefined,
        })),
    [gatewaysData],
  );

  return { sensors3p, gateways3p, loading: sensorsLoading || gatewaysLoading };
}

export { GET_SENSORS_3P, GET_GATEWAYS_3P };

export function useSensors3pGateways3pMutations() {
  const [createSensors3pMutation] = useMutation<
    StudioCreateSensors3pMutation,
    StudioCreateSensors3pMutationVariables
  >(CREATE_SENSORS_3P, {
    errorPolicy: 'all',
    context: { uri: INTEGRATIONS_API_URL, skipErrorToast: true },
  });

  const [updateSensors3pMutation] = useMutation<
    StudioUpdateSensors3pMutation,
    StudioUpdateSensors3pMutationVariables
  >(UPDATE_SENSORS_3P, {
    errorPolicy: 'all',
    context: { uri: INTEGRATIONS_API_URL, skipErrorToast: true },
  });

  const [deleteSensors3pMutation] = useMutation<
    StudioDeleteSensors3pMutation,
    StudioDeleteSensors3pMutationVariables
  >(DELETE_SENSORS_3P, {
    errorPolicy: 'all',
    context: { uri: INTEGRATIONS_API_URL, skipErrorToast: true },
  });

  const [createGateways3pMutation] = useMutation<
    StudioCreateGateways3pMutation,
    StudioCreateGateways3pMutationVariables
  >(CREATE_GATEWAYS_3P, {
    errorPolicy: 'all',
    context: { uri: INTEGRATIONS_API_URL, skipErrorToast: true },
  });

  const [updateGateways3pMutation] = useMutation<
    StudioUpdateGateways3pMutation,
    StudioUpdateGateways3pMutationVariables
  >(UPDATE_GATEWAYS_3P, {
    errorPolicy: 'all',
    context: { uri: INTEGRATIONS_API_URL, skipErrorToast: true },
  });

  const [deleteGateways3pMutation] = useMutation<
    StudioDeleteGateways3pMutation,
    StudioDeleteGateways3pMutationVariables
  >(DELETE_GATEWAYS_3P, {
    errorPolicy: 'all',
    context: { uri: INTEGRATIONS_API_URL, skipErrorToast: true },
  });

  return {
    createSensors3pMutation,
    updateSensors3pMutation,
    deleteSensors3pMutation,
    createGateways3pMutation,
    updateGateways3pMutation,
    deleteGateways3pMutation,
  };
}
